import { ScopeId } from '@interfaces/base';
import type {
  GlobalSearchResponse,
  NcFile,
  ProductionOrderSearchResponse,
  ProductionOperationSearchResponse,
  NcFileSearchResponse,
  PartSearchResponse,
  RecipeSearchResponse,
  RecipeOperationSearchResponse,
  ProductionBatchSearchResponse,
  MachineEventCycleSearchResponse,
  ClampingTool as DomainClampingTool,
  User as DomainUser,
  ProductionBatchStatisticsResponse,
  Document,
} from '../api/domain/types';

export enum LinkStatus {
  NOT_LINKED = 'NotLinked',
  SINGLE_LINK = 'SingleLink',
  MULTI_LINK = 'MultiLink',
  PARTIAL_LINK = 'PartialLink',
}

// === Domain-derived types ===

export type NCFileVersions = NonNullable<NcFile['versions']>;
export type NCFileVersion = NCFileVersions[number];

export type GlobalSearchItem = NonNullable<GlobalSearchResponse['searchResult']>[number];
export type ProductionOrderSearchItem = NonNullable<ProductionOrderSearchResponse['searchResult']>[number];
export type OperationSearchItem = NonNullable<ProductionOperationSearchResponse['searchResult']>[number];
export type RecipeOperationSearchItem = NonNullable<RecipeOperationSearchResponse['searchResult']>[number];
export type NCFileSearchItem = NonNullable<NcFileSearchResponse['searchResult']>[number];
export type NCFileVersionNumber = NonNullable<NCFileSearchItem['versions']>[number];
export type PartSearchItem = NonNullable<PartSearchResponse['searchResult']>[number];
export type RecipeSearchItem = NonNullable<RecipeSearchResponse['searchResult']>[number];
export type ProductionBatch = NonNullable<ProductionBatchSearchResponse['searchResult']>[number];
export type MachineEventCycles = NonNullable<MachineEventCycleSearchResponse['searchResult']>[number];

export type ClampingTool = DomainClampingTool;
export type User = DomainUser;
export type UserProfile = NonNullable<DomainUser['userProfile']>;
export type CompanyProfile = NonNullable<DomainUser['companyProfile']>;

export type Link = NonNullable<ProductionBatch['links']>[number];
export type LinkedNCFile = NonNullable<OperationSearchItem['linkedNcFiles']>[number];
export type MachineLink = NonNullable<OperationSearchItem['machines']>[number];
export type ImageData = NonNullable<ProductionOrderSearchItem['imageData']>;
export type NCFileLink = NonNullable<NCFileSearchItem['links']>[number];

// === App-specific types (no API equivalent) ===

export interface RecipeOperation {
  description: string;
  operationNumber: string;
  setupDescription: string;
  shortDescription: string;
  sortOrder: number;
}
export interface NCFileSmartLink {
  isActive: boolean;
  machineId: number;
  ncFile: string | null;
  ncFileId: number;
  ncSmartLinkId: number;
  partId: number;
  productionOperationId: number;
  productionOrderId: number | null;
  recipeId: number | null;
  recipeOperationId: number | null;
}

export interface ItemKeyFields {
  name: string;
  value: string;
}

export type ItemType = 'ProductionOrder' | 'Part' | 'Recipe';

export interface TimeLetter {
  d?: string | number;
  h: string | number;
  m: string | number;
  s: string | number;
}

export interface NCFileData {
  ncFileId: number;
  ncFileName: string;
  ncFileStatus: string;
  versions: NCFileVersions;
  part: {
    partId: number | null;
    partDrawingPosition: string | null;
    partNumber: string | null;
    partDrawingNumber: string | null;
    partDescription: string | null;
    partDrawingRevision: string | null;
  };
  ideal: {
    timeType: string;
    runtime: TimeLetter;
    changeTime: TimeLetter;
    setupTime: TimeLetter;
  };
  paid: {
    timeType: string;
    runtime: TimeLetter;
    changeTime: TimeLetter;
    setupTime: TimeLetter;
  };
  batch: ProductionBatch | null;
  productionBatchId: string | undefined;
}

export interface HistoryPool {
  resetRecipe: boolean;
  resetProductionOrder: boolean;
  resetPart: boolean;
  resetSelectedLevel: boolean;
}

export type ProductionBatchStatistics = ProductionBatchStatisticsResponse['statistics'];

export interface ProductionBatchFunction {
  searchResult: ProductionBatch[];
}

export type ImageLink = NonNullable<Document['links']>[number];
export type ImageTag = NonNullable<Document['tags']>[number];

export interface ImageParams {
  documentId: number | undefined;
  imageHash?: string;
  width?: number;
  height?: number;
  contextId: number;
  contextCode: string;
  imageData: {
    scopeId: ScopeId;
    isCropped: boolean | undefined;
    extensionDerivative: string | undefined;
    pageNumber: number | undefined;
    rotationAngle: number | undefined;
    rotationAngleCrop: number | undefined;
  };
}
